import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SupportChat, type SupportContext } from '@web-front/support-chat';
import { useAppSelector } from '../../hooks/redux';
import { selectCurrentToken, selectUser } from '../../store/auth.slice';
import { useSupportMeetingWorkflow } from './useSupportMeetingWorkflow';
import { useSupportNavigationWorkflow } from './useSupportNavigationWorkflow';
import { useSupportWorkflowResume } from './useSupportWorkflowResume';

const SUPPORT_API_BASE_URL = (import.meta as ImportMeta & { env?: Record<string, string> }).env
  ?.VITE_SUPPORT_API_BASE_URL;

/** Floating support entry point shared by every authenticated cabinet page. */
export function AppSupportChat() {
  const token = useAppSelector(selectCurrentToken);
  const user = useAppSelector(selectUser);
  const location = useLocation();
  const navigate = useNavigate();
  const getAccessToken = useCallback(() => token || null, [token]);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const localDate = new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date());
  const meetingWorkflow = useSupportMeetingWorkflow(token || null, timeZone, localDate);
  const navigationWorkflow = useSupportNavigationWorkflow(token || null);
  const workflowResume = useSupportWorkflowResume(meetingWorkflow, navigationWorkflow);
  const isConsultations = location.pathname === '/consultations';
  const context = useMemo<SupportContext>(
    () => ({
      source: 'dashboard',
      viewer: { role: user ? 'psychologist' : 'client', category: user?.category || undefined },
      user: user
        ? {
            id: user.id != null ? String(user.id) : undefined,
            displayName: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || undefined,
          }
        : undefined,
      page: { pathname: location.pathname },
      app: {
        environment: (import.meta as ImportMeta & { env?: Record<string, string> }).env?.MODE || 'production',
      },
      scheduling: { timeZone, localDate },
    }),
    [localDate, location.pathname, timeZone, user],
  );

  const apiBaseUrl =
    SUPPORT_API_BASE_URL ||
    ((import.meta as ImportMeta & { env?: Record<string, string> }).env?.MODE === 'production'
      ? (() => {
          throw new Error('VITE_SUPPORT_API_BASE_URL must be configured in production');
        })()
      : 'http://localhost:3040');

  return (
    <div className={isConsultations ? 'zg-support-chat--consultations' : undefined}>
      <SupportChat
        context={context}
        apiBaseUrl={apiBaseUrl}
        getAccessToken={getAccessToken}
        locale="ru"
        meetingWorkflow={meetingWorkflow}
        navigationWorkflow={navigationWorkflow}
        workflowResume={workflowResume}
        onOpenTicket={(ticketId) => navigate(ticketId ? `/support-tickets/${ticketId}` : '/support-tickets')}
      />
    </div>
  );
}
